// REL_1.1.14 F-262 / S-982 — Ported from deedo-frontend AboutContactPage.tsx.
// REL_1.1.15 F-262 §6 — Mobile contact form optimization: single-column on mobile + ≥44pt
// tap targets + sticky-bottom submit on mobile + aria-disabled + aria-live status region +
// inline loading spinner (per spec §6.2 + §6.3 a11y discipline).
// Differences from REL_1.1.14:
//   - Drops @tanstack/react-query + useToastStore (not in deedo-marketing deps) — uses plain
//     useState submission state + inline form feedback (success/error banner above button)
//   - Drops axios; uses fetch (cross-origin contactService.ts)
//   - Adds country: 'us' field per S-986 (DBA 054 ContactInquiries.Country)
//   - Privacy Policy link cross-domains to {appBaseUrl}/privacy-policy per F-262 §1.1 user-direct
//   - Canonical URL: /about (REL_1.1.15 S-1101 v2.8 — `/us/` prefix dropped; `/us/about` 301→/contact)
import { useRef, useState, type FormEvent } from "react";
import { Loader2 } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { MapPin, Mail } from "lucide-react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { submitContactInquiry, RateLimitError } from "../../services/contactService";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY ?? "";
const TURNSTILE_EXECUTE_TIMEOUT_MS = 3000;
const MARKETING_BASE = import.meta.env.VITE_MARKETING_BASE_URL || "https://www.deedo.ai";
const APP_BASE_URL: string = import.meta.env.VITE_APP_BASE_URL || "https://app.deedo.ai";

const INQUIRY_TYPES = [
  "General Inquiry",
  "Sales — Agent / Brokerage",
  "Support",
  "Press",
  "Partnership",
];

type SubmitState = "idle" | "verifying" | "submitting" | "success" | "rate-limited" | "error";

export default function About() {
  const [inquiryType, setInquiryType] = useState(INQUIRY_TYPES[0]!);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitState, setSubmitState] = useState<SubmitState>("idle");

  const turnstileRef = useRef<TurnstileInstance | null>(null);
  const tokenResolveRef = useRef<((value: string | null) => void) | null>(null);

  function resolveToken(token: string | null) {
    const resolver = tokenResolveRef.current;
    if (resolver) {
      tokenResolveRef.current = null;
      resolver(token);
    }
  }

  function executeTurnstile(): Promise<string | null> {
    if (!TURNSTILE_SITE_KEY || !turnstileRef.current) return Promise.resolve(null);
    return new Promise<string | null>((resolve) => {
      tokenResolveRef.current = resolve;
      try {
        turnstileRef.current?.reset();
        turnstileRef.current?.execute();
      } catch {
        resolveToken(null);
        return;
      }
      window.setTimeout(() => resolveToken(null), TURNSTILE_EXECUTE_TIMEOUT_MS);
    });
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitState === "verifying" || submitState === "submitting") return;

    setSubmitState("verifying");
    const token = await executeTurnstile();
    setSubmitState("submitting");

    try {
      await submitContactInquiry({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        message: message.trim(),
        inquiryType,
        cfTurnstileResponse: token,
        country: "us", // F-262 S-986: per /us/* page-scope
      });
      setSubmitState("success");
      setFirstName("");
      setLastName("");
      setEmail("");
      setMessage("");
      setInquiryType(INQUIRY_TYPES[0]!);
      turnstileRef.current?.reset();
    } catch (err) {
      if (err instanceof RateLimitError) {
        setSubmitState("rate-limited");
      } else {
        setSubmitState("error");
      }
    }
  }

  const isBusy = submitState === "verifying" || submitState === "submitting";

  return (
    <>
      <Helmet>
        <title>About &amp; Contact — Deedo</title>
        <meta
          name="description"
          content="Deedo bridges physical properties with digital intelligence. Built in Silicon Valley. Get in touch with our team — we usually reply in two hours."
        />
        <link rel="canonical" href={`${MARKETING_BASE}/about`} />
        <meta property="og:title" content="About & Contact — Deedo" />
        <meta property="og:description" content="Get in touch — we usually reply in two hours." />
        <meta property="og:url" content={`${MARKETING_BASE}/about`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <section className="bg-gray-50 py-20">
        <div className="mx-auto grid max-w-6xl gap-12 px-4 lg:grid-cols-2 lg:px-8">
          {/* LEFT — story + contact info */}
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight text-[var(--brand-primary)] lg:text-5xl">
              Bridging Physical
              <br />
              Properties with
              <br />
              Digital Intelligence.
            </h1>

            <div className="mt-8 space-y-5 text-gray-600">
              <p className="leading-relaxed">
                Deedo was founded on a simple premise: getting answers about a property shouldn&apos;t
                require waiting for a callback. By combining advanced AI processing with the
                simplicity of QR codes, we are bringing operational transparency to real estate.
              </p>
              <p className="leading-relaxed">
                We empower buyers to explore confidently and allow agents to automate the tedious
                parts of the job, focusing instead on building human connections. Proudly built in
                Silicon Valley.
              </p>
            </div>

            <div className="mt-10 space-y-5">
              <ContactBlock Icon={MapPin} title="Headquarters" body="Silicon Valley, California" />
              <ContactBlock
                Icon={Mail}
                title="Email Support"
                body={
                  <>
                    <a href="mailto:support@deedo.ai" className="hover:underline">
                      support@deedo.ai
                    </a>{" "}
                    <span className="text-xs text-gray-400">(Usually replies in 2 hrs)</span>
                  </>
                }
              />
              <p className="text-sm text-gray-500">
                By submitting, you agree to our{" "}
                <a
                  href={`${APP_BASE_URL}/privacy-policy`}
                  className="underline hover:text-[var(--brand-primary)]"
                >
                  Privacy Policy
                </a>
                .
              </p>
            </div>
          </div>

          {/* RIGHT — contact form */}
          <div id="contact" className="rounded-2xl bg-white p-6 shadow-md md:p-8">
            <h2 className="text-2xl font-bold text-[var(--brand-primary)]">Get in Touch</h2>

            {/* Inline submit-state banners. aria-live region announces state transitions to AT
                even when no role="alert" is present (e.g., success path). */}
            <div aria-live="polite" aria-atomic="true">
              {submitState === "success" && (
                <div
                  role="status"
                  className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
                >
                  Thank you for reaching out! We&apos;ll be in touch within 2 business days.
                </div>
              )}
              {submitState === "rate-limited" && (
                <div
                  role="alert"
                  className="mt-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800"
                >
                  Too many submissions — please try again later.
                </div>
              )}
              {submitState === "error" && (
                <div
                  role="alert"
                  className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
                >
                  Something went wrong. Please try again or email{" "}
                  <a href="mailto:support@deedo.ai" className="underline">
                    support@deedo.ai
                  </a>{" "}
                  directly.
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="mt-6 space-y-5">
              <div>
                <label htmlFor="inquiry-type" className="text-sm font-medium text-gray-700">
                  Inquiry Type
                </label>
                <select
                  id="inquiry-type"
                  value={inquiryType}
                  onChange={(e) => setInquiryType(e.target.value)}
                  className="mt-1.5 block min-h-11 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-base focus:border-[var(--brand-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-secondary)]/30 sm:text-sm"
                >
                  {INQUIRY_TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-3">
                <div>
                  <label htmlFor="first-name" className="text-sm font-medium text-gray-700">
                    First Name
                  </label>
                  <input
                    id="first-name"
                    type="text"
                    autoComplete="given-name"
                    required
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Jane"
                    className="mt-1.5 block min-h-11 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-[var(--brand-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-secondary)]/30 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="last-name" className="text-sm font-medium text-gray-700">
                    Last Name
                  </label>
                  <input
                    id="last-name"
                    type="text"
                    autoComplete="family-name"
                    required
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    className="mt-1.5 block min-h-11 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-[var(--brand-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-secondary)]/30 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@example.com"
                  className="mt-1.5 block min-h-11 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-[var(--brand-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-secondary)]/30 sm:text-sm"
                />
              </div>

              <div>
                <label htmlFor="message" className="text-sm font-medium text-gray-700">
                  Message
                </label>
                <textarea
                  id="message"
                  rows={4}
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="How can we help?"
                  className="mt-1.5 block w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-[var(--brand-secondary)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-secondary)]/30 sm:text-sm"
                />
              </div>

              {/* Sticky-bottom submit on mobile (<sm = <640px); flows normally on tablet+ */}
              <div className="sticky bottom-0 -mx-6 -mb-6 border-t border-gray-100 bg-white/95 px-6 py-4 backdrop-blur sm:static sm:m-0 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-0">
                <button
                  type="submit"
                  disabled={isBusy}
                  aria-disabled={isBusy}
                  aria-label="Send message"
                  className="flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-[var(--brand-primary)] py-3 text-sm font-semibold text-white transition-colors hover:bg-[var(--brand-primary-light)] disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isBusy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  {submitState === "verifying"
                    ? "Verifying…"
                    : submitState === "submitting"
                      ? "Sending…"
                      : "Send Message"}
                </button>
              </div>

              {TURNSTILE_SITE_KEY && (
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  options={{
                    execution: "execute",
                    appearance: "interaction-only",
                    size: "invisible",
                  }}
                  onSuccess={(token) => resolveToken(token)}
                  onError={() => resolveToken(null)}
                  onExpire={() => resolveToken(null)}
                  className="hidden"
                />
              )}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

function ContactBlock({
  Icon,
  title,
  body,
}: {
  Icon: typeof MapPin;
  title: string;
  body: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-4">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--brand-secondary)]/20">
        <Icon className="h-4 w-4 text-[var(--brand-primary)]" aria-hidden="true" />
      </span>
      <div>
        <h3 className="text-sm font-bold text-[var(--brand-primary)]">{title}</h3>
        <div className="mt-0.5 text-sm text-gray-600">{body}</div>
      </div>
    </div>
  );
}
